import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { useCreateTenant, useUpdateTenant } from '../hooks/useTenants';
import { TenantFormData } from '../types';
import { TenantTableData } from '../types';

interface AddTenantDialogProps {
  open: boolean;
  onClose: () => void;
  editingItem?: TenantTableData | null;
  onSuccess?: () => void;
}

export const AddTenantDialog: React.FC<AddTenantDialogProps> = ({
  open,
  onClose,
  editingItem,
  onSuccess,
}) => {
  const isEditing = !!editingItem;
  const createTenantMutation = useCreateTenant();
  const updateTenantMutation = useUpdateTenant();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<TenantFormData>({
    mode: 'onChange',
    defaultValues: {
      name: editingItem?.name || '',
      description: editingItem?.description || '',
      code: editingItem?.code || '',
    },
  });

  // Reset form when dialog opens/closes or editing item changes
  React.useEffect(() => {
    if (open) {
      reset({
        name: editingItem?.name || '',
        description: editingItem?.description || '',
        code: editingItem?.code || '',
      });
    }
  }, [open, editingItem, reset]);

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = async (data: TenantFormData) => {
    try {
      if (isEditing && editingItem) {
        await updateTenantMutation.mutateAsync({
          tenantId: editingItem.id,
          data: {
            name: data.name,
            description: data.description || undefined,
            code: data.code || undefined,
          },
        });
      } else {
        await createTenantMutation.mutateAsync({
          name: data.name,
          description: data.description || undefined,
          code: data.code || undefined,
        });
      }

      // Close dialog and reset form on success
      handleClose();
      onSuccess?.();
    } catch (error) {
      // Error handling is done in the mutation hooks via snackbar
      console.error('Failed to save tenant:', error);
    }
  };

  const isLoading = createTenantMutation.isPending || updateTenantMutation.isPending;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={isLoading}
    >
      <DialogTitle>{isEditing ? 'Edit University' : 'Add New University'}</DialogTitle>

      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Controller
              name="name"
              control={control}
              rules={{
                required: 'University name is required',
                minLength: {
                  value: 2,
                  message: 'University name must be at least 2 characters',
                },
                maxLength: {
                  value: 100,
                  message: 'University name must not exceed 100 characters',
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="University Name"
                  variant="outlined"
                  fullWidth
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  disabled={isLoading}
                  // eslint-disable-next-line jsx-a11y/no-autofocus -- Intentional: focus first field when dialog opens for keyboard UX
                  autoFocus
                />
              )}
            />

            <Controller
              name="code"
              control={control}
              rules={{
                maxLength: {
                  value: 10,
                  message: 'University code must not exceed 10 characters',
                },
                pattern: {
                  value: /^[A-Z0-9]*$/,
                  message: 'University code must contain only uppercase letters and numbers',
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="University Code"
                  variant="outlined"
                  fullWidth
                  error={!!errors.code}
                  helperText={errors.code?.message}
                  disabled={isLoading}
                  placeholder="e.g., UIO, NTNU, UIB"
                />
              )}
            />

            <Controller
              name="description"
              control={control}
              rules={{
                maxLength: {
                  value: 500,
                  message: 'Description must not exceed 500 characters',
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Description"
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={3}
                  error={!!errors.description}
                  helperText={errors.description?.message}
                  disabled={isLoading}
                  placeholder="Optional description for the university..."
                />
              )}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} disabled={isLoading} color="inherit">
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!isValid || isLoading}
            startIcon={isLoading ? <CircularProgress size={16} /> : undefined}
          >
            {isLoading
              ? isEditing
                ? 'Updating...'
                : 'Creating...'
              : isEditing
                ? 'Update University'
                : 'Create University'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
